import { Tag, Typography } from '@quickbit/qb-design-system';
import { CodeBlock } from '../CodeBlock';
import { InlineCode } from '../InlineCode';
import { PageText } from './types';

const createProcessingFlowOrderExample = `{
  "amount": 100,
  "currency": "EUR", // <-- EUR is the only currency supported in processing flow
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "use_processing_flow": true, // <-- This flag toggles on the processing flow
  "email": "roger@quickbit.com", // <-- This is required in the processing flow
  "purchased_at": "2023-04-12T06:23:03.558Z" // <-- When the fiat payment was made
}`;

const createProcessingFlowOrderResponseExample = `{
  "amount": 100,
  "currency": "EUR",
  "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  "created_at": "2023-04-12T06:23:07.558Z",
  "reference_id": null,
  "status": "PENDING",
  "creation_amount": 100
}`;

const apiKeyExample = `{
  "X-Api-Key": "your-api-key-here"
}`;

export const processingFlowDocumentation: Array<PageText> = [
  {
    id: 'processing-flow',
    title: 'Processing flow',
    page: 'integration',
    subPage: 'processing-flow',
    content: <Typography variant="heading1">Processing flow</Typography>,
  },
  {
    id: 'processing-flow-1',
    title: 'Processing flow',
    page: 'integration',
    subPage: 'processing-flow',
    content: (
      <>
        <Typography>
          The processing flow is a way to receive money spent by a customer
          through a blockchain transaction, without the customer needing to
          interface with crypto themselves. It works in a similar fashion to the
          regular Quickbit Pay checkout flow, but instead of the customer being
          sent to the Quickbit Pay checkout page, the transaction is sent behind
          the scenes to a customer account, identified by the customer&apos;s
          email address, and then automatically sent to pay for an order that is
          settled to your account.
        </Typography>
        <br />
      </>
    ),
  },
  {
    id: 'creating-a-processing-order',
    title: 'Creating a processing order',
    page: 'integration',
    subPage: 'processing-flow',
    content: (
      <Typography variant="heading2">Creating a processing order</Typography>
    ),
  },
  {
    id: 'processing-flow-endpoint',
    title: 'Processing Flow Endpoint',
    page: 'integration',
    subPage: 'processing-flow',
    content: (
      <>
        <Typography variant="body">
          Processing flow orders are created using the{' '}
          <InlineCode>POST /api/pay/v1/order</InlineCode> endpoint. This
          endpoint requires authentication using your API key in the{' '}
          <InlineCode>X-Api-Key</InlineCode> header.
        </Typography>
        <CodeBlock language="json" code={apiKeyExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'processing-flow-request-body',
    title: 'Request Body',
    page: 'integration',
    subPage: 'processing-flow',
    content: <Typography variant="subtitle">Request Body</Typography>,
  },
  {
    id: 'processing-flow-request-body-content',
    title: 'Request Body',
    page: 'integration',
    subPage: 'processing-flow',
    content: (
      <>
        <Typography variant="body">
          The request body should contain the order details as JSON. Required
          fields include <InlineCode>amount</InlineCode>,{' '}
          <InlineCode>currency</InlineCode>,{' '}
          <InlineCode>use_processing_flow</InlineCode>, and{' '}
          <InlineCode>email</InlineCode>, while{' '}
          <InlineCode>callback_url</InlineCode>,{' '}
          <InlineCode>reference_id</InlineCode>, and{' '}
          <InlineCode>purchased_at</InlineCode> are optional but recommended.
        </Typography>
        <ul>
          <li>
            <InlineCode>amount</InlineCode> - The amount to charge (decimal)
          </li>
          <li>
            <InlineCode>currency</InlineCode> - Must be EUR (only supported
            currency)
          </li>
          <li>
            <InlineCode>use_processing_flow</InlineCode> - Set to true to enable
            processing flow
          </li>
          <li>
            <InlineCode>email</InlineCode> - Customer email address (required)
          </li>
          <li>
            <InlineCode>callback_url</InlineCode> - URL to receive order status
            updates (optional)
          </li>
          <li>
            <InlineCode>reference_id</InlineCode> - Your internal order ID
            (optional)
          </li>
          <li>
            <InlineCode>purchased_at</InlineCode> - Timestamp when fiat payment
            was made (optional)
          </li>
        </ul>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-a-processing-order-1',
    title: 'Creating a processing order',
    page: 'integration',
    subPage: 'processing-flow',
    content: (
      <>
        <Typography>
          After making sure that the customer is allowed, and that their card
          transaction has been successful, an order must be created in the
          Quickbit Pay system using the processing flow endpoint. This is done
          by sending a POST request to{' '}
          <InlineCode>/api/pay/v1/order</InlineCode> with the{' '}
          <InlineCode>use_processing_flow</InlineCode> flag set to{' '}
          <InlineCode>true</InlineCode> in the request body. The{' '}
          <InlineCode>email</InlineCode> parameter is required for processing
          flow orders, and <InlineCode>EUR</InlineCode> is the only supported
          currency. If you recorded the time that the fiat payment was made,
          please include it in the <InlineCode>purchased_at</InlineCode>{' '}
          parameter. See the example below.
        </Typography>
        <CodeBlock language="json" code={createProcessingFlowOrderExample} />
        <Typography>
          The response will contain the order details including the unique order
          identifier (uuid), status, amounts, and other relevant information.
          Note that processing flow orders do not include a checkout URL since
          there is no user interaction required. See the example response below.
        </Typography>
        <CodeBlock
          language="json"
          code={createProcessingFlowOrderResponseExample}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-a-processing-order-2',
    title: 'Creating a processing order',
    page: 'integration',
    subPage: 'processing-flow',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography>
          The processing flow will create orders in Algorand.
        </Typography>
        <br></br>
      </>
    ),
  },
];
